import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Calculator, DollarSign, TrendingUp, Users, Clock, 
  ArrowRight, CheckCircle2, Building2, Stethoscope,
  PieChart, BarChart3, RefreshCw
} from 'lucide-react';
import { Slider } from "../components/ui/slider";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const ROICalculatorPage = () => {
  const [activeTab, setActiveTab] = useState('front-office');

  // --- Front Office State ---
  const [foInputs, setFoInputs] = useState({
    missedCalls: 50,         // Calls missed per month
    newPatientValue: 1500,   // LTV of a new patient
    monthlyProduction: 80000,// Total monthly production
    staffHourlyRate: 25,     // Hourly wage for front desk
    hygieneAppts: 150,       // Hygiene appointments per month
    verifications: 200,      // Ins verifications per month
  });

  // --- Back Office State ---
  const [boInputs, setBoInputs] = useState({
    monthlyCollections: 80000,
    denialRate: 10,           // %
    claimsPerMonth: 300,
    avgClaimValue: 250,
    billingStaffHours: 40,    // Hours per week spent on billing/collections
    billingStaffRate: 28,
  });

  const [foResults, setFoResults] = useState({
    missedCallRevenue: 0,
    staffTimeSavings: 0,
    scheduleFillRevenue: 0,
    totalMonthlyValue: 0,
    annualROI: 0
  });

  const [boResults, setBoResults] = useState({
    denialRecovery: 0,
    billingTimeSavings: 0,
    overheadReduction: 0,
    totalMonthlyValue: 0,
    annualROI: 0
  });

  // --- Calculation Logic ---

  useEffect(() => {
    // Front Office Calculations
    // 1. Missed Calls: Assume Agent answers 100%. Assume 20% conversion to appointment.
    const callRevenue = foInputs.missedCalls * 0.20 * foInputs.newPatientValue;

    // 2. Staff Time Savings:
    //    - Scheduling calls: ~5 mins per call (Amy)
    //    - Confirmations/Recall: ~10 hours/month (Leena/Stephanie)
    //    - Insurance Verification: ~15 mins per verification (Amanda)
    const verificationHours = (foInputs.verifications * 15) / 60;
    const callHours = (foInputs.missedCalls * 3 * 5) / 60; // Assume 3x total calls vs missed
    // Conservative total savings
    const totalHoursSaved = verificationHours + 15; // +15 for general admin/scheduling
    const timeSavings = totalHoursSaved * foInputs.staffHourlyRate;

    // 3. Schedule Filling (Leena): Assume 3% increase in production due to better utilization
    const fillRevenue = foInputs.monthlyProduction * 0.03;

    const totalFO = callRevenue + timeSavings + fillRevenue;

    setFoResults({
      missedCallRevenue: Math.round(callRevenue),
      staffTimeSavings: Math.round(timeSavings),
      scheduleFillRevenue: Math.round(fillRevenue),
      totalMonthlyValue: Math.round(totalFO),
      annualROI: Math.round(totalFO * 12)
    });

  }, [foInputs]);

  useEffect(() => {
    // Back Office Calculations
    // 1. Denial Recovery: Assume Agents reduce denial rate by 40% (e.g. 10% -> 6%)
    //    Recovered Revenue = (Claims * AvgValue * DenialRate) * 0.40
    const totalClaimsValue = boInputs.claimsPerMonth * boInputs.avgClaimValue;
    const currentDenialLoss = totalClaimsValue * (boInputs.denialRate / 100);
    const recoveredRevenue = currentDenialLoss * 0.40;

    // 2. Billing Time Savings: Assume 70% automation of manual tasks
    const weeklyHoursSaved = boInputs.billingStaffHours * 0.70;
    const monthlyTimeSavings = weeklyHoursSaved * 4 * boInputs.billingStaffRate;

    // 3. Overhead/Collection Efficiency: Assume 1% better collection rate overall
    const collectionEfficiency = boInputs.monthlyCollections * 0.01;

    const totalBO = recoveredRevenue + monthlyTimeSavings + collectionEfficiency;

    setBoResults({
      denialRecovery: Math.round(recoveredRevenue),
      billingTimeSavings: Math.round(monthlyTimeSavings),
      overheadReduction: Math.round(collectionEfficiency),
      totalMonthlyValue: Math.round(totalBO),
      annualROI: Math.round(totalBO * 12)
    });
  }, [boInputs]);

  // Format currency
  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

        <div className="container mx-auto max-w-7xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-400/20 text-blue-300 text-xs font-bold uppercase tracking-wider mb-6">
            <Calculator className="w-4 h-4" />
            ROI Calculator
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            See How Much You Could <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
              Save & Earn
            </span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
            Calculate the potential return on investment for your dental practice by implementing our AI agent suites. Adjust the inputs below to match your practice metrics.
          </p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 px-6 -mt-10 relative z-20">
        <div className="container mx-auto max-w-6xl">
          <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
            
            {/* Tabs */}
            <div className="flex border-b border-gray-100">
              <button
                onClick={() => setActiveTab('front-office')}
                className={`flex-1 py-6 text-center font-bold text-lg transition-colors flex items-center justify-center gap-2 ${
                  activeTab === 'front-office' 
                    ? 'bg-white text-blue-600 border-b-2 border-blue-600' 
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                <Users className="w-5 h-5" />
                Front Office Suite
              </button>
              <button
                onClick={() => setActiveTab('back-office')}
                className={`flex-1 py-6 text-center font-bold text-lg transition-colors flex items-center justify-center gap-2 ${
                  activeTab === 'back-office' 
                    ? 'bg-white text-purple-600 border-b-2 border-purple-600' 
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                <Building2 className="w-5 h-5" />
                Back Office Suite
              </button>
            </div>

            <div className="p-8 lg:p-12">
              {activeTab === 'front-office' ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
                  {/* Inputs */}
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <Stethoscope className="w-5 h-5 text-blue-600" />
                        Practice Inputs
                      </h3>
                      
                      <div className="space-y-6">
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <Label>Monthly Production</Label>
                            <span className="font-bold text-blue-600">{formatMoney(foInputs.monthlyProduction)}</span>
                          </div>
                          <Slider 
                            value={[foInputs.monthlyProduction]} 
                            min={20000} max={500000} step={5000} 
                            onValueChange={(v) => setFoInputs({...foInputs, monthlyProduction: v[0]})}
                            className="py-2"
                          />
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <Label>Missed Calls per Month</Label>
                            <span className="font-bold text-blue-600">{foInputs.missedCalls}</span>
                          </div>
                          <Slider 
                            value={[foInputs.missedCalls]} 
                            min={0} max={500} step={5} 
                            onValueChange={(v) => setFoInputs({...foInputs, missedCalls: v[0]})} 
                          />
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <Label>New Patient Value (LTV)</Label>
                            <span className="font-bold text-blue-600">{formatMoney(foInputs.newPatientValue)}</span>
                          </div>
                          <Slider 
                            value={[foInputs.newPatientValue]} 
                            min={500} max={5000} step={100} 
                            onValueChange={(v) => setFoInputs({...foInputs, newPatientValue: v[0]})} 
                          />
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <Label>Staff Hourly Rate</Label>
                            <span className="font-bold text-blue-600">${foInputs.staffHourlyRate}/hr</span>
                          </div>
                          <Slider 
                            value={[foInputs.staffHourlyRate]} 
                            min={15} max={60} step={1} 
                            onValueChange={(v) => setFoInputs({...foInputs, staffHourlyRate: v[0]})} 
                          />
                        </div>

                         <div className="space-y-3">
                          <div className="flex justify-between">
                            <Label>Insurance Verifications / Mo</Label>
                            <span className="font-bold text-blue-600">{foInputs.verifications}</span>
                          </div>
                          <Slider 
                            value={[foInputs.verifications]} 
                            min={50} max={1000} step={10} 
                            onValueChange={(v) => setFoInputs({...foInputs, verifications: v[0]})} 
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Results */}
                  <div className="bg-slate-50 rounded-3xl p-8 border border-slate-100 flex flex-col justify-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-8 flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-green-600" />
                      Estimated Monthly Impact
                    </h3>

                    <div className="space-y-6 mb-10">
                      <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
                            <Users className="w-5 h-5" />
                          </div>
                          <span className="text-gray-600 font-medium">New Patient Revenue</span>
                        </div>
                        <span className="font-bold text-gray-900 text-lg">+{formatMoney(foResults.missedCallRevenue)}</span>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="bg-purple-100 p-2 rounded-lg text-purple-600">
                            <Clock className="w-5 h-5" />
                          </div>
                          <span className="text-gray-600 font-medium">Staff Time Savings</span>
                        </div>
                        <span className="font-bold text-gray-900 text-lg">+{formatMoney(foResults.staffTimeSavings)}</span>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="bg-green-100 p-2 rounded-lg text-green-600">
                            <TrendingUp className="w-5 h-5" />
                          </div>
                          <span className="text-gray-600 font-medium">Schedule Optimization</span>
                        </div>
                        <span className="font-bold text-gray-900 text-lg">+{formatMoney(foResults.scheduleFillRevenue)}</span>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-8">
                      <div className="flex justify-between items-end mb-2">
                        <span className="text-gray-500 font-medium uppercase tracking-wide text-sm">Total Monthly Value</span>
                        <span className="text-4xl font-extrabold text-blue-600">{formatMoney(foResults.totalMonthlyValue)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-400">Annual projected impact</span>
                        <span className="font-bold text-gray-700">{formatMoney(foResults.annualROI)} / year</span>
                      </div>
                    </div>
                    
                    <Link to="/book-demo" className="mt-8">
                      <button className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg shadow-lg shadow-blue-200 transition-all transform hover:-translate-y-1">
                        Get Started with Asha
                      </button>
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
                  {/* Back Office Inputs */}
                  <div className="space-y-8">
                     <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-purple-600" />
                        Billing Inputs
                      </h3>
                      
                      <div className="space-y-6">
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <Label>Monthly Collections</Label>
                            <span className="font-bold text-purple-600">{formatMoney(boInputs.monthlyCollections)}</span>
                          </div>
                          <Slider 
                            value={[boInputs.monthlyCollections]} 
                            min={20000} max={500000} step={5000} 
                            onValueChange={(v) => setBoInputs({...boInputs, monthlyCollections: v[0]})} 
                          />
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <Label>Claims per Month</Label>
                            <span className="font-bold text-purple-600">{boInputs.claimsPerMonth}</span>
                          </div>
                          <Slider 
                            value={[boInputs.claimsPerMonth]} 
                            min={50} max={1000} step={10} 
                            onValueChange={(v) => setBoInputs({...boInputs, claimsPerMonth: v[0]})} 
                          />
                        </div>

                         <div className="space-y-3">
                          <div className="flex justify-between">
                            <Label>Current Denial Rate</Label>
                            <span className="font-bold text-purple-600">{boInputs.denialRate}%</span>
                          </div>
                          <Slider 
                            value={[boInputs.denialRate]} 
                            min={0} max={50} step={1} 
                            onValueChange={(v) => setBoInputs({...boInputs, denialRate: v[0]})} 
                          />
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <Label>Billing Staff Hours / Week</Label>
                            <span className="font-bold text-purple-600">{boInputs.billingStaffHours} hrs</span>
                          </div>
                          <Slider 
                            value={[boInputs.billingStaffHours]} 
                            min={0} max={160} step={5} 
                            onValueChange={(v) => setBoInputs({...boInputs, billingStaffHours: v[0]})} 
                          />
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <Label>Billing Staff Hourly Rate</Label>
                            <span className="font-bold text-purple-600">${boInputs.billingStaffRate}/hr</span>
                          </div>
                          <Slider 
                            value={[boInputs.billingStaffRate]} 
                            min={15} max={80} step={1} 
                            onValueChange={(v) => setBoInputs({...boInputs, billingStaffRate: v[0]})} 
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Back Office Results */}
                  <div className="bg-slate-50 rounded-3xl p-8 border border-slate-100 flex flex-col justify-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-8 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-green-600" />
                      Estimated Monthly Impact
                    </h3>

                    <div className="space-y-6 mb-10">
                      <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="bg-red-100 p-2 rounded-lg text-red-600">
                            <RefreshCw className="w-5 h-5" />
                          </div>
                          <span className="text-gray-600 font-medium">Recovered Denials</span>
                        </div>
                        <span className="font-bold text-gray-900 text-lg">+{formatMoney(boResults.denialRecovery)}</span>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
                            <Clock className="w-5 h-5" />
                          </div>
                          <span className="text-gray-600 font-medium">Labor Savings</span>
                        </div>
                        <span className="font-bold text-gray-900 text-lg">+{formatMoney(boResults.billingTimeSavings)}</span>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="bg-green-100 p-2 rounded-lg text-green-600">
                            <DollarSign className="w-5 h-5" />
                          </div>
                          <span className="text-gray-600 font-medium">Collection Efficiency</span>
                        </div>
                        <span className="font-bold text-gray-900 text-lg">+{formatMoney(boResults.overheadReduction)}</span>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-8">
                      <div className="flex justify-between items-end mb-2">
                        <span className="text-gray-500 font-medium uppercase tracking-wide text-sm">Total Monthly Value</span>
                        <span className="text-4xl font-extrabold text-purple-600">{formatMoney(boResults.totalMonthlyValue)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-400">Annual projected impact</span>
                        <span className="font-bold text-gray-700">{formatMoney(boResults.annualROI)} / year</span>
                      </div>
                    </div>
                    
                    <Link to="/book-demo" className="mt-8">
                      <button className="w-full py-4 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-lg shadow-lg shadow-purple-200 transition-all transform hover:-translate-y-1">
                        Automate Your Back Office
                      </button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-12 text-center text-sm text-gray-500">
            <p>* These calculations are estimates based on industry averages and typical client results. Actual results may vary based on your practice's specific situation.</p>
          </div>
        </div>
      </section>

      {/* Agents Reassurance Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Real Results, Real Impact</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              See why dental practices trust Asha to manage their operations.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
               <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                 <CheckCircle2 className="w-6 h-6 text-blue-600" />
               </div>
               <h3 className="text-xl font-bold text-gray-900 mb-2">98% Retention Rate</h3>
               <p className="text-gray-600">Our clients stay with us because they see immediate value in their first month.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
               <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                 <TrendingUp className="w-6 h-6 text-purple-600" />
               </div>
               <h3 className="text-xl font-bold text-gray-900 mb-2">30% Revenue Growth</h3>
               <p className="text-gray-600">Average year-over-year growth for practices using our full suite of agents.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ROICalculatorPage;
